import { AudioTextDecoder } from "./audio-text-decoder";
import { WebSocketClient } from "./web-socket-client";
import {
  StreamerError,
  SDKError,
  createUnknownError,
  withErrorData,
} from "../types/error.types";
import type { StreamData, StreamFormat } from "../types/model.types";
import { getDeviceFingerPrint } from "../utils/device-finger-print";
import { audioDumper } from "../utils/audio-dumper";

export interface StreamerConfig {
  url: string;
  subscribeId: string;
  streamId?: string;
  format: StreamFormat;
  onStreamData?: (streamData: StreamData) => void;
  onError?: (error: SDKError) => void;
  onConnected?: () => void;
}

/**
 * A class that combines WebSocket connection and audio/text decoding functionality
 */
export class AudioTextStreamer {
  private wsClient: WebSocketClient;
  private decoder: AudioTextDecoder;

  constructor(config: StreamerConfig) {
    // Validate required configuration
    if (!config.url || !config.subscribeId) {
      const missingFields = [];
      if (!config.url) missingFields.push("url");
      if (!config.subscribeId) missingFields.push("subscribeId");

      const configError = withErrorData(StreamerError.InvalidConfiguration, {
        missingFields,
      });
      config.onError?.(configError);
      throw configError;
    }

    // Initialize the decoder
    this.decoder = new AudioTextDecoder({
      format: config.format,
      onStreamData: (streamData) => {
        config.onStreamData?.(streamData);
      },
      onError: (error: SDKError) => {
        config.onError?.(error);
      },
    });

    // Initialize the WebSocket client
    this.wsClient = new WebSocketClient({
      url: config.url,
      subscribeId: config.subscribeId,
      deviceFingerprint: getDeviceFingerPrint(),
      onMessage: (data) => {
        try {
          if (data instanceof ArrayBuffer) {
            audioDumper.capture(data);
            this.decoder.decode(data, Date.now());
          }
        } catch (error) {
          // Handle stream processing errors
          const streamProcessingError = withErrorData(
            StreamerError.StreamProcessingFailed,
            {
              streamId: config.streamId,
              reason: error instanceof Error ? error.message : String(error),
            },
          );
          config.onError?.(streamProcessingError);
        }
      },
      onError: (error: SDKError) => {
        // Forward WebSocket errors directly
        config.onError?.(error);
      },
      onOpen: () => config.onConnected?.(),
      onClose: () => {}, // config.onDisconnected?.()
    });
  }

  /**
   * Starts the stream by connecting to the WebSocket server
   */
  start(): void {
    try {
      this.wsClient.connect();
    } catch (error) {
      const streamError = createUnknownError(error, {
        context: "streamer_start",
        operation: "connect",
      });
      // Note: config.onError is not accessible here, so we throw
      throw streamError;
    }
  }

  /**
   * Stops the stream by disconnecting from the WebSocket server
   */
  stop(): void {
    this.wsClient.disconnect();
  }

  /**
   * Resets the decoder state
   */
  reset(): void {
    this.decoder.reset();
  }

  /**
   * Checks if the streamer is currently connected
   */
  isConnected(): boolean {
    return this.wsClient.isConnected();
  }
}
